export const convertUnit = (unit, value) => {
  switch (unit.toLowerCase()) {
    case "degrees":
    case "degrees/second":
      return value; // Assuming value is already in degrees or degrees/sec
    case "inches":
      return value / 25.4; // Convert mm to inches (1 inch = 25.4 mm)
    case "mph":
      return value * 0.002237; // Convert mm/s to mph (1 mm/s = 0.002237 mph)
    case "m/s/s":
      return value / 1000; // Convert mm/s^2 to m/s^2
    case "g":
    case "gs":
      return value / 9806.65; // Convert mm/s^2 to g (1 g ≈ 9806.65 mm/s^2)
    case "seconds":
      return value;
    case "ms":
      return value * 1000;
    case "percent":
      return value * 100; // Convert ratio to percentage
    case "ratio":
      return value; // Assuming ratio is unitless
    default:
      return value; // Return the original value if no conversion is needed
  }
};

export const renderUnitType = (unit, value, showSymbol = true) => {
  if (value === "n/a") return value;
  const parsedUnit = unit ? unit.toLowerCase() : "";
  const parsedValue = value ? +value : 0;

  // Convert the value based on the unit
  const convertedValue = convertUnit(parsedUnit, parsedValue);

  switch (parsedUnit) {
    case "degrees":
      return `${convertedValue.toFixed()}${showSymbol ? "°" : ""}`;
    case "inches":
      return `${convertedValue.toFixed(1)}"`;
    case "mm":
      return `${convertedValue.toFixed()} ${showSymbol ? "mm" : ""}`;
    case "seconds":
      return `${convertedValue.toFixed(2)} ${showSymbol ? "sec" : ""}`;
    case "ratio":
      return `${convertedValue.toFixed(2)} ${showSymbol ? "ratio" : ""}`;
    case "ms":
      return `${convertedValue.toFixed()} ${showSymbol ? "ms" : ""}`;
    case "percent":
      return `${convertedValue.toFixed()}${showSymbol ? "%" : ""}`;
    case "mph":
      return `${convertedValue.toFixed(1)} ${showSymbol ? "mph" : ""}`;
    case "m/s/s":
      return `${convertedValue.toFixed(2)} ${showSymbol ? "m/s²" : ""}`;
    case "g":
    case "gs":
      return `${convertedValue.toFixed(1)} ${showSymbol ? "g" : ""}`;
    case "degrees/second":
      return `${convertedValue.toFixed()} ${showSymbol ? "d/s" : ""}`;
    case "order":
      return `${convertedValue.toFixed()}`;
    default:
      return convertedValue.toFixed();
  }
};

export const renderZScore = (unit, value) => {
  if (value === "n/a") return value;
  const parsedUnit = unit ? unit.toLowerCase() : "";
  const parsedValue = value ? +value : 0;

  // Convert the value based on the unit
  const convertedValue = convertUnit(parsedUnit, parsedValue);

  switch (parsedUnit) {
    case "degrees":
      return `${convertedValue.toFixed()}`;
    case "inches":
      return `${convertedValue.toFixed(1)}`;
    case "mm":
      return `${convertedValue.toFixed()}`;
    case "seconds":
      return `${convertedValue.toFixed(2)}`;
    case "ratio":
      return `${convertedValue.toFixed(2)}`;
    case "ms":
      return `${convertedValue.toFixed()}`;
    case "percent":
      return `${convertedValue.toFixed()}`;
    case "mph":
      return `${convertedValue.toFixed(1)}`;
    case "m/s/s":
      return `${convertedValue.toFixed(2)}`;
    case "g":
    case "gs":
      return `${convertedValue.toFixed(1)}`;
    case "degrees/second":
      return `${convertedValue.toFixed()}`;
    case "order":
      return `${convertedValue.toFixed()}`;
    default:
      return convertedValue.toFixed();
  }
};

export const summarizeIndicatorsByClub = (
  videoArray,
  selectedTableData,
  tourReference = {}
) => {
  const result = {
    Driver: {},
    Iron: {},
  };

  const initialized = {
    Driver: new Set(),
    Iron: new Set(),
  };

  const clubMap = {
    D: "Driver",
    default: "Iron",
  };

  const videosToAnalyse = selectedTableData.length
    ? selectedTableData
    : videoArray;

  // Step 1: Process videos
  videosToAnalyse.forEach((video) => {
    const analysis = video.analysis;
    if (!analysis) return;

    const clubType = clubMap[video.userData?.golfClub] || "Iron";

    for (const [key, data] of Object.entries(analysis)) {
      if (!result[clubType][key]) {
        result[clubType][key] = { avg: 0, zScore: 0, count: 0 };
      }

      result[clubType][key].avg += data.indicatorValue;
      result[clubType][key].zScore += data.zScore?.value || 0;
      result[clubType][key].count += 1;

      initialized[clubType].add(key);
    }
  });

  // Step 2: Gather all indicator keys
  const allKeys = new Set([
    ...(tourReference?.driver || []).map((d) => d.indicatorId),
    ...(tourReference?.iron || []).map((d) => d.indicatorId),
    ...Object.keys(result.Driver),
    ...Object.keys(result.Iron),
  ]);

  // Helper to get indicatorRange
  function getIndicatorRange(clubType, indicatorId) {
    const source =
      clubType === "Driver" ? tourReference.driver : tourReference.iron;
    return (
      source?.find((d) => d.indicatorId === indicatorId)?.indicatorRange ?? 0
    );
  }

  // Step 3: Ensure all keys exist, finalize structure
  for (const clubType of ["Driver", "Iron"]) {
    for (const key of allKeys) {
      if (!initialized[clubType].has(key)) {
        // Not initialized — default 0s
        const range = getIndicatorRange(clubType, key);
        result[clubType][key] = {
          avg: 0,
          zScore: 0,
          tourAvg: 0 - range,
        };
      } else {
        // Finalize value
        const { avg, zScore } = result[clubType][key];
        const range = getIndicatorRange(clubType, key);
        result[clubType][key] = {
          avg,
          zScore,
          tourAvg: avg - range,
        };
      }
    }
  }

  return result;
};
